import { Chart, ChartConfiguration, ChartOptions } from "chart.js";
import { IBroadastStatusData, IBroadcast, IDashboardStorageData, IDashboardUserData } from "../models";
import ChartDataLabels from 'chartjs-plugin-datalabels';

export class Dashboard {

    // STORAGE DOUGHNUT CHART
    public static initializeStorageDoughnutChart(storageData: IDashboardStorageData) {
        const canvas = document.getElementById('storageChart') as HTMLCanvasElement;
        if (!canvas) return;

        // Destroy existing chart if it exists
        const existingChart = Chart.getChart(canvas);
        if (existingChart) existingChart.destroy();

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const toGB = (bytes: number) => +(bytes / (1024 ** 3)).toFixed(2);

        const usedGB = toGB(storageData.business_used_storage);
        const totalGB = toGB(storageData.business_total_storage);
        const freeGB = totalGB - usedGB;

        new Chart(ctx, {
            type: 'doughnut',
            data: {
                labels: ['Used', 'Free'],
                datasets: [
                    {
                        data: [usedGB, freeGB],
                        backgroundColor: ['#10B981', '#F3F4F6'], // Green and light grey to match the mockup
                        borderWidth: 0,
                        hoverOffset: 4
                    }
                ]
            },
            options: {
                cutout: '85%',
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { display: false },
                    datalabels: { display: false },
                    tooltip: {
                        callbacks: {
                            label: function (context) {
                                return `${context.label}: ${context.parsed} GB`;
                            }
                        }
                    }
                }
            } as ChartOptions<'doughnut'>
        });
    }

    // SCHEDULE HEALTH DOUGHNUT CHART
    public static initializeScheduleHealthDoughnutChart(percentScheduled: number) {
        const canvas = document.getElementById('scheduleHealthChart') as HTMLCanvasElement;
        if (!canvas) return;

        // Destroy existing chart if it exists
        const existingChart = Chart.getChart(canvas);
        if (existingChart) existingChart.destroy();

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        new Chart(ctx, {
            type: 'doughnut',
            data: {
                labels: ['Scheduled', 'Unscheduled'],
                datasets: [
                    {
                        data: [percentScheduled, 100 - percentScheduled],
                        backgroundColor: ['#10B981', '#F3F4F6'], // Green and light grey
                        borderWidth: 0,
                        hoverOffset: 4
                    }
                ]
            },
            options: {
                cutout: '85%',
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { display: false },
                    datalabels: { display: false },
                    tooltip: {
                        callbacks: {
                            label: function (context) {
                                return `${context.label}: ${context.parsed}%`;
                            }
                        }
                    }
                }
            } as ChartOptions<'doughnut'>
        });
    }

    // SCREEN STATUS DOUGHNUT CHART
    public static initializeScreenStatusDoughnutChart(data: any) {
        const canvas = document.getElementById('screenStatusChart') as HTMLCanvasElement;
        if (!canvas) return;

        const existingChart = Chart.getChart(canvas);
        if (existingChart) existingChart.destroy();

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        new Chart(ctx, {
            type: 'doughnut',
            data: {
                labels: ['Online', 'Standby', 'Inactive', 'Offline'],
                datasets: [
                    {
                        data: [
                            data.online_screens || 0,
                            data.standby_screens || 0, // Mock standby if not present
                            data.inactive_screens || 0, // Mock inactive
                            data.offline_screens || 0
                        ],
                        backgroundColor: ['#10B981', '#F59E0B', '#9CA3AF', '#EF4444'], // Green, Orange, Grey, Red
                        borderWidth: 0,
                        hoverOffset: 4
                    }
                ]
            },
            options: {
                cutout: '80%',
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { display: false },
                    datalabels: { display: false },
                    tooltip: {
                        callbacks: {
                            label: function (context) {
                                return `${context.label}: ${context.parsed}`;
                            }
                        }
                    }
                }
            } as ChartOptions<'doughnut'>
        });
    }

    // SCHEDULE BAR CHART HORIZONTAL
    public static initializeScheduleBarChart(chart: Chart, broadcast_status_data: IBroadastStatusData) {
        const canvas = document.getElementById('statusBarChart') as HTMLCanvasElement;
        if (canvas) {
            const ctx = canvas.getContext('2d');
            if (ctx) {
                // Create gradients for each bar
                const gradients = [
                    this.createGradient(ctx, '#FFD180', '#FFCC80'),
                    this.createGradient(ctx, '#90CAF9', '#64B5F6'),
                    this.createGradient(ctx, '#B9F6CA', '#69F0AE')
                ];

                const maxValue = Math.max(
                    broadcast_status_data.in_progress_broadcasts,
                    broadcast_status_data.upcoming_broadcasts,
                    broadcast_status_data.completed_broadcasts
                );

                const suggestedMax = maxValue === 0 ? 1 : Math.ceil(maxValue * 1.2);

                chart = new Chart(ctx, {
                    type: 'bar',
                    data: {
                        labels: [
                            'In-Progress',
                            'Upcoming',
                            'Completed'
                        ],
                        datasets: [{
                            data: [
                                broadcast_status_data.in_progress_broadcasts,
                                broadcast_status_data.upcoming_broadcasts,
                                broadcast_status_data.completed_broadcasts
                            ],
                            backgroundColor: gradients,
                            borderRadius: 18,
                            borderSkipped: false
                        }]
                    },
                    options: {
                        indexAxis: 'y',
                        responsive: true,
                        plugins: {
                            legend: { display: false },
                            tooltip: {
                                enabled: true,
                                callbacks: {
                                    title: function () {
                                        return [];
                                    },
                                    label: function (context) {
                                        // Combine label and value in a single line
                                        return context.label + ': ' + context.formattedValue;
                                    }
                                }
                            },
                            datalabels: {
                                anchor: 'end',
                                align: 'end',
                                color: (context) => gradients[context.dataIndex],
                                font: { weight: 'bold', size: 14 },
                                formatter: (value) => value
                            }
                        },
                        scales: {
                            x: {
                                max: suggestedMax
                            },
                            y: {
                                grid: { display: false },
                                ticks: {
                                    align: 'inner',
                                    crossAlign: 'far',
                                    font: { weight: 'bold', size: 16 }
                                }
                            }
                        }
                    },
                    plugins: [this.dataLabelsPlugin()]
                });
            }
        }
    }

    // Custom plugin for datalabels at end of bars
    public static dataLabelsPlugin() {
        return {
            id: 'customDataLabels',
            afterDatasetsDraw: (chart: any) => {
                const ctx = chart.ctx;
                chart.data.datasets[0].data.forEach((value: any, index: number) => {
                    const meta = chart.getDatasetMeta(0);
                    const bar = meta.data[index];
                    if (bar) {
                        ctx.save();
                        ctx.font = 'bold 14px Arial';
                        ctx.fillStyle = '#888';
                        ctx.textBaseline = 'middle';
                        ctx.textAlign = 'left';
                        ctx.fillText(
                            value as string,
                            bar.x + 8,
                            bar.y
                        );
                        ctx.restore();
                    }
                });
            }
        };
    }

    // Helper to create gradient
    public static createGradient(ctx: CanvasRenderingContext2D, color1: string, color2: string) {
        const gradient = ctx.createLinearGradient(0, 0, 250, 0);
        gradient.addColorStop(0, color1);
        gradient.addColorStop(1, color2);
        return gradient;
    }

    // USER PIE CHART
    public static initializeUserPieChart(userData: IDashboardUserData) {
        const config: ChartConfiguration<'pie'> = {
            type: 'pie',
            data: {
                labels: [
                    `Unassigned (${userData.business_role_counts.unassigned_users ?? 0})`,
                    `Editor (${userData.business_role_counts.Editor ?? 0})`,
                    `Manager (${userData.business_role_counts.Manager ?? 0})`,
                    `Partner (${userData.business_role_counts.Partner ?? 0})`
                ],
                datasets: [{
                    data: [
                        userData.business_role_counts.unassigned_users ?? 0,
                        userData.business_role_counts.Editor ?? 0,
                        userData.business_role_counts.Manager ?? 0,
                        userData.business_role_counts.Partner ?? 0
                    ],
                    backgroundColor: ['#a259ff', '#fdbc64', '#407bff', '#63cdff'],
                    borderWidth: 3
                }]
            },
            options: {
                responsive: true,
                plugins: {
                    legend: {
                        display: true,
                        position: 'bottom',
                        labels: {
                            usePointStyle: false,
                            pointStyle: 'circle',
                            padding: 10
                        }
                    },
                    datalabels: { display: false },
                }
            }
        };

        new Chart('roleChart', config);
    }

    //SCHEDULE DAY VIEW LINE CHART
    public static initializeScheduleLineChart(scheduleProgressData: IBroadcast[]) {
        const canvas = document.getElementById('timelineChart') as HTMLCanvasElement;
        if (!canvas) return;

        const container = document.getElementById('timelineChartContainer');

        // Remove previous chart if any
        const existingChart = Chart.getChart(canvas);
        if (existingChart) {
            existingChart.destroy();
        }

        const ctx = canvas.getContext('2d');

        // Helpers...
        const parseTimeToHour = (timeStr: string) => {
            const [h, m, s] = timeStr.split(':').map(Number);
            return h + m / 60 + s / 3600;
        };
        const formatHour = (hour: number) => {
            const h = Math.floor(hour);
            const suffix = h < 12 ? 'AM' : 'PM';
            const displayHour = h % 12 === 0 ? 12 : h % 12;
            return `${displayHour.toString().padStart(2, '0')}:00 ${suffix}`;
        };
        const createGradient = (ctx: CanvasRenderingContext2D, color1: string, color2: string) => {
            const gradient = ctx.createLinearGradient(0, 0, 0, 400);
            gradient.addColorStop(0, color1);
            gradient.addColorStop(1, color2);
            return gradient;
        };
        const isExactTwelve = (timeStr: string) => {
            return timeStr === "00:00:00" || timeStr === "23:59:59";
        };

        // Prepare data
        let chartData = scheduleProgressData.map(item => ({
            x: item.name,
            y: [parseTimeToHour(item.start_time), parseTimeToHour(item.end_time)],
            start_time: item.start_time,
            end_time: item.end_time
        }));

        let noDataMode = false;
        if (chartData.length === 0) {
            noDataMode = true;
            chartData = [{
                x: 'No Data',
                y: [0, 0],
                start_time: '00:00:00',
                end_time: '00:00:00'
            }];
        }

        // Dynamic bar width and scroll setup
        const barCount = chartData.length;
        let barPercentage = 1.0;
        let categoryPercentage = 1.0;
        if (barCount > 15) {
            barPercentage = 0.7;
            categoryPercentage = 0.7;
        }
        if (barCount > 30) {
            barPercentage = 0.5;
            categoryPercentage = 0.5;
        }

        // Set container min-width for horizontal scroll if needed
        const minBarWidth = 40;
        if (container) {
            container.style.minWidth = `${Math.max(barCount * minBarWidth, container.clientWidth)}px`;
            // Set vertical scroll and fixed height for 12AM-4PM (16 hours)
            container.style.height = '720px'; // Adjust as needed (30px per hour x 16 = 480px)
            container.style.overflowY = 'auto';
            container.style.position = 'relative';
        }
        if (canvas) {
            // Set canvas height to fit all 24 hours (so you can scroll to see the rest)
            canvas.style.height = '1000px'; // 30px per hour x 24 = 720px
            canvas.height = 1000; // for Chart.js internal scaling
        }

        if (ctx) {
            const gradients = [
                createGradient(ctx, '#FFB347', '#FFCC33'),
                createGradient(ctx, '#BA68C8', '#FFD54F'),
                createGradient(ctx, '#FFD54F', '#FFB347'),
                createGradient(ctx, '#4FC3F7', '#81C784'),
                createGradient(ctx, '#29B6F6', '#00E5FF'),
                createGradient(ctx, '#7E57C2', '#FFB347'),
            ];

            const data = {
                datasets: [
                    {
                        label: 'Timeline',
                        data: chartData,
                        backgroundColor: (context: any) =>
                            noDataMode ? 'rgba(200,200,200,0.5)' : gradients[context.dataIndex % gradients.length],
                        borderRadius: (context: any) => {
                            const dataIndex = context.dataIndex;
                            const dataItem = chartData[dataIndex];
                            const radius = 20;
                            const bottomIsTwelve = isExactTwelve(dataItem.end_time);
                            return {
                                topLeft: 0,
                                topRight: 0,
                                bottomLeft: bottomIsTwelve ? 0 : radius,
                                bottomRight: bottomIsTwelve ? 0 : radius,
                            };
                        },
                        borderSkipped: false,
                        barPercentage,
                        categoryPercentage,
                        barThickness: 28,
                    }
                ]
            };

            const options: any = {
                responsive: true, // Important: disables Chart.js resizing so our scroll works
                maintainAspectRatio: false,
                indexAxis: 'x' as const,
                plugins: {
                    datalabels: {
                        color: noDataMode ? '#888' : 'white',
                        anchor: 'center',
                        align: 'center',
                        rotation: 90,
                        font: {
                            weight: 'bold',
                            size: 12,
                        },
                        formatter: (value: any, context: any) => {
                            if (noDataMode) return '';
                            return context.chart.data.datasets[0].data[context.dataIndex].x;
                        }
                    },
                    legend: { display: false },
                    tooltip: {
                        enabled: !noDataMode,
                        callbacks: {
                            label: (context: any) => {
                                if (noDataMode) return '';
                                const [start, end] = context.raw.y;
                                return `${context.raw.x}: ${formatHour(start)} - ${formatHour(end)}`;
                            }
                        }
                    }
                },
                scales: {
                    x: {
                        type: 'category',
                        grid: { display: false },
                        ticks: { display: false }
                    },
                    y: {
                        min: 0,
                        max: 24,
                        reverse: true,
                        ticks: {
                            stepSize: 1,
                            callback: (value: number) => formatHour(value),
                        },
                        grid: { display: true }
                    }
                }
            };

            new Chart(ctx, {
                type: 'bar',
                data,
                options,
                plugins: [ChartDataLabels],
            });

            // Scroll to show only 12AM-4PM by default
            if (container) {
                // Each hour is 30px high (720px/24)
                // To show 0-16 (16 hours): scrollTop = 0
                // To show 8AM-12PM (for example): scrollTop = 8*30 = 240
                container.scrollTop = 0; // Start at top (12AM)
            }
        }
    }






}
